'use client'

import { useState, useEffect } from 'react'
import { useCurrency } from '@/lib/currency'
import { commissionApi } from '@/lib/api'
import type { CommissionHistoryEntry } from '@/types'
import { DollarSign, TrendingUp, Download, Percent, BarChart3 } from 'lucide-react'

interface CommissionManagementProps {
  onUpdate?: () => void
}

export default function CommissionManagement(_: CommissionManagementProps) {
  const { format } = useCurrency()
  const [loading, setLoading] = useState(true)
  const [commissionRate, setCommissionRate] = useState<number>(0.10)
  const [totalCommission, setTotalCommission] = useState<number>(0)
  const [commissionByLandlord, setCommissionByLandlord] = useState<any[]>([])
  const [monthlyBreakdown, setMonthlyBreakdown] = useState<any[]>([])
  const [history, setHistory] = useState<CommissionHistoryEntry[]>([])
  const [dateRange, setDateRange] = useState<'all' | 'month' | 'year'>('all')
  const [showHistory, setShowHistory] = useState(false)

  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null

  // Load commission rate
  const loadCommissionRate = async () => {
    if (!token) return
    
    try {
      const response = await commissionApi.getRate(token)
      if (response.success && response.data) {
        setCommissionRate((response.data as any).commissionRate || 0.10)
      }
    } catch (error) {
      console.error('Error loading commission rate:', error)
    }
  }

  // Load commission statistics
  const loadStats = async () => {
    if (!token) return
    
    try {
      const now = new Date()
      let startDate: string | undefined
      
      if (dateRange === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      } else if (dateRange === 'year') {
        startDate = new Date(now.getFullYear(), 0, 1).toISOString()
      }
      
      const response = await commissionApi.getStats(token, { startDate })
      
      if (response.success && response.data) {
        const data = response.data as any
        setTotalCommission(data.totalCommission || 0)
        setCommissionByLandlord(data.commissionByLandlord || [])
        setMonthlyBreakdown(data.monthlyBreakdown || [])
      }
    } catch (error) {
      console.error('Error loading commission stats:', error)
    }
  }

  // Load commission history
  const loadHistory = async () => {
    if (!token) return
    
    try {
      const response = await commissionApi.getHistory(token)
      if (response.success && response.data) {
        setHistory((response.data as any).history || [])
      }
    } catch (error) {
      console.error('Error loading commission history:', error)
    }
  }

  // Download commission report
  const downloadReport = async (format: 'json' | 'csv' = 'csv') => {
    if (!token) return
    
    try {
      const now = new Date()
      let startDate: string | undefined
      
      if (dateRange === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      } else if (dateRange === 'year') {
        startDate = new Date(now.getFullYear(), 0, 1).toISOString()
      }
      
      const response = await commissionApi.getReport(token, { startDate, format })
      
      if (response.success && response.data) {
        if (format === 'csv') {
          // Create download link for CSV
          const blob = new Blob([response.data as string], { type: 'text/csv' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `commission-report-${Date.now()}.csv`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        } else {
          // Download JSON
          const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `commission-report-${Date.now()}.json`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        }
      }
    } catch (error) {
      console.error('Error downloading report:', error)
      alert('Failed to download report')
    }
  }

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true)
      await Promise.all([
        loadCommissionRate(),
        loadStats(),
        loadHistory()
      ])
      setLoading(false)
    }
    
    loadAll()
  }, [dateRange])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading commission data...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Commission Management</h2>
          <p className="text-gray-600 mt-1">View commission statistics and reports</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <button
              onClick={() => setDateRange('all')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                dateRange === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Time
            </button>
            <button
              onClick={() => setDateRange('year')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                dateRange === 'year' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              This Year
            </button>
            <button
              onClick={() => setDateRange('month')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                dateRange === 'month' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              This Month
            </button>
          </div>
          <button
            onClick={() => downloadReport('csv')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Current Commission Rate */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm mb-1">Current Commission Rate</p>
            <p className="text-4xl font-bold">{((commissionRate || 0.10) * 100).toFixed(1)}%</p>
            <p className="text-blue-100 text-sm mt-2">
              Note: Commission rate is managed in Platform Settings
            </p>
          </div>
          <Percent className="w-16 h-16 text-blue-200 opacity-50" />
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Total Commission Collected</h3>
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{format(totalCommission)}</p>
          <p className="text-xs text-gray-500 mt-1">Selected period</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Top Landlords</h3>
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{commissionByLandlord.length}</p>
          <p className="text-xs text-gray-500 mt-1">Landlords with commission</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Monthly Breakdown</h3>
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{monthlyBreakdown.length}</p>
          <p className="text-xs text-gray-500 mt-1">Months of data</p>
        </div>
      </div>

      {/* Commission by Landlord */}
      {commissionByLandlord.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Commission by Landlord</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Landlord</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Commission</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Gross</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payments</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {commissionByLandlord.slice(0, 10).map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.landlordName || 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.landlordEmail || 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-red-600">
                      {format(item.totalCommission || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {format(item.totalGross || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.paymentCount || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Breakdown */}
      {monthlyBreakdown.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Monthly Commission Breakdown</h3>
          <div className="space-y-3">
            {monthlyBreakdown.map((item, index) => {
              const monthName = new Date(item._id.year, item._id.month - 1).toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })
              return (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{monthName}</p>
                    <p className="text-sm text-gray-500">{item.paymentCount || 0} payments</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-600">{format(item.totalCommission || 0)}</p>
                    <p className="text-sm text-gray-500">Gross: {format(item.totalGross || 0)}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Commission History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Commission Rate Change History</h3>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {showHistory ? 'Hide' : 'Show'} History
          </button>
        </div>
        
        {showHistory && (
          <div className="space-y-3">
            {history.length > 0 ? (
              history.map((entry, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">
                      Changed from {(entry.details.oldRate || 0) * 100}% to {(entry.details.newRate || 0) * 100}%
                    </p>
                    <p className="text-sm text-gray-500">
                      {entry.details.reason || 'No reason provided'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(entry.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No commission rate changes recorded</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
